<template>
  <div class="max-w-6xl w-full mx-auto space-y-6">
    <div>
      <h1 class="text-2xl font-bold text-gray-900">User Management</h1>
      <p class="mt-1 text-sm text-gray-600">Manage admin-panel access for this tenant.</p>
    </div>

    <div class="rounded-lg border bg-white p-4">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div class="md:col-span-2">
          <label class="block text-xs text-gray-600 mb-1">Search</label>
          <input
            v-model="query"
            type="text"
            class="border rounded px-3 py-2 w-full"
            placeholder="Name or email"
            @input="onSearchInput"
          />
        </div>
        <div>
          <label class="block text-xs text-gray-600 mb-1">University</label>
          <select v-model="universityFilter" class="border rounded px-3 py-2 w-full" @change="fetchUsers">
            <option value="">All universities</option>
            <option v-for="university in universities" :key="university.slug" :value="university.slug">
              {{ university.name }}
            </option>
          </select>
        </div>
        <div class="flex items-end gap-2">
          <button
            type="button"
            class="px-3 py-2 rounded text-sm border bg-white hover:bg-gray-50"
            :disabled="loading"
            @click="fetchUsers"
          >
            Refresh
          </button>
          <button
            type="button"
            class="px-3 py-2 rounded text-sm border border-blue-700 bg-blue-700 text-white hover:bg-blue-800"
            @click="openCreate"
          >
            New User
          </button>
        </div>
      </div>
      <div v-if="status" class="mt-3 text-sm" :class="statusError ? 'text-red-600' : 'text-green-700'">
        {{ status }}
      </div>
    </div>

    <div class="border rounded-lg bg-white overflow-hidden">
      <table class="min-w-full text-sm">
        <thead class="bg-gray-50 text-gray-700">
          <tr>
            <th class="text-left px-3 py-2">Name</th>
            <th class="text-left px-3 py-2">Email</th>
            <th class="text-left px-3 py-2">Roles</th>
            <th class="text-left px-3 py-2">Universities</th>
            <th class="text-left px-3 py-2">Status</th>
            <th class="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6" class="px-3 py-6 text-center text-gray-500">Loading users...</td>
          </tr>
          <tr v-else-if="!users.length">
            <td colspan="6" class="px-3 py-6 text-center text-gray-500">No users found.</td>
          </tr>
          <tr v-for="user in visibleUsers" :key="user.id" class="border-t">
            <td class="px-3 py-2 font-medium text-gray-900">{{ user.name || 'Unnamed user' }}</td>
            <td class="px-3 py-2">{{ user.email }}</td>
            <td class="px-3 py-2">{{ formatRoles(user) }}</td>
            <td class="px-3 py-2">{{ formatUniversities(user.universities, user) }}</td>
            <td class="px-3 py-2">
              <span
                class="inline-flex items-center rounded px-2 py-1 text-xs"
                :class="user.disabled ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'"
              >
                {{ user.disabled ? 'Disabled' : 'Active' }}
              </span>
            </td>
            <td class="px-3 py-2">
              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  class="px-2 py-1 rounded border bg-white hover:bg-gray-50"
                  @click="openEdit(user)"
                >
                  Edit
                </button>
                <button
                  type="button"
                  class="px-2 py-1 rounded border bg-white hover:bg-gray-50"
                  :disabled="saving"
                  @click="resetPassword(user)"
                >
                  Reset
                </button>
                <button
                  type="button"
                  class="px-2 py-1 rounded border border-red-300 text-red-700 bg-white hover:bg-red-50"
                  :disabled="saving || user.disabled"
                  @click="disableUser(user)"
                >
                  Disable
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="editing" class="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div class="w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <form @submit.prevent="saveUser">
          <div class="border-b px-5 py-4">
            <h2 class="text-lg font-semibold text-gray-900">{{ editing.id ? 'Edit User' : 'New User' }}</h2>
          </div>
          <div class="space-y-4 px-5 py-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input v-model="editing.name" type="text" class="border rounded px-3 py-2 w-full" required />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                v-model="editing.email"
                type="email"
                class="border rounded px-3 py-2 w-full"
                :disabled="Boolean(editing.id)"
                required
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Roles</label>
              <p class="mb-2 text-xs text-gray-500">Leave all roles unchecked to create a source-upload-only user for the selected universities.</p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label v-for="role in roleOptions" :key="role" class="flex items-center gap-2 text-sm">
                  <input v-model="editing.roles" type="checkbox" :value="role" />
                  <span>{{ role === 'sales:read:cross-tenant' ? 'sales:read:cross-tenant — configured partner universities' : role }}</span>
                </label>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Universities</label>
              <label class="mb-2 flex items-center gap-2 text-sm">
                <input v-model="editing.allUniversities" type="checkbox" />
                <span>All universities</span>
              </label>
              <div class="max-h-48 overflow-auto rounded border p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label v-for="university in universities" :key="university.slug" class="flex items-center gap-2 text-sm">
                  <input
                    v-model="editing.universities"
                    type="checkbox"
                    :value="university.slug"
                    :disabled="editing.allUniversities"
                  />
                  <span>{{ university.name }} ({{ university.slug }})</span>
                </label>
              </div>
            </div>
            <div v-if="formError" class="text-sm text-red-600">{{ formError }}</div>
          </div>
          <div class="border-t px-5 py-4 flex justify-end gap-2">
            <button type="button" class="px-3 py-2 rounded border bg-white hover:bg-gray-50" @click="closeEditor">
              Cancel
            </button>
            <button
              type="submit"
              class="px-3 py-2 rounded border border-blue-700 bg-blue-700 text-white hover:bg-blue-800"
              :disabled="saving"
            >
              {{ saving ? 'Saving...' : 'Save User' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      universities: [],
      query: '',
      universityFilter: '',
      loading: false,
      saving: false,
      status: '',
      statusError: false,
      formError: '',
      searchDebounceTimer: null,
      editing: null,
      roleOptions: [
        'university-admin',
        'analytics-viewer',
        'analytics_viewer',
        'social-media',
        'orders:read',
        'sales:read',
        'analytics:orders',
        'analytics:read',
        'analytics-exporter',
        'survey-exporter',
        'survey-editor',
        'import:read',
        'import:write',
        'snapshot-upload',
        'semantic-search',
        'mgc-gmail-support',
        'support:read',
        'support:delivery',
      ],
    }
  },
  created() {
    this.initialLoad()
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
  },
  computed: {
    visibleUsers() {
      return this.loading ? [] : this.users
    },
  },
  methods: {
    async initialLoad() {
      await this.fetchUniversities()
      await this.fetchUsers()
    },
    async fetchUniversities() {
      try {
        const response = await axios.get('/universities')
        this.universities = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.status = 'Could not load universities'
        this.statusError = true
      }
    },
    onSearchInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => this.fetchUsers(), 250)
    },
    async fetchUsers() {
      this.loading = true
      this.status = ''
      this.statusError = false
      try {
        const payload = {
          mode: 'users_list',
          limit: 200,
        }
        const query = String(this.query || '').trim()
        if (query) payload.query = query
        if (this.universityFilter) payload.universitySlugs = [this.universityFilter]
        const response = await axios.post('/admin/search', payload)
        this.users = Array.isArray(response?.data?.users) ? response.data.users : []
        if (Array.isArray(response?.data?.grantable_roles)) {
          this.roleOptions = response.data.grantable_roles
        }
      } catch (error) {
        this.status = (error && error.response && error.response.data && error.response.data.error) || 'Could not load users'
        this.statusError = true
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.formError = ''
      this.editing = {
        id: '',
        name: '',
        email: '',
        roles: ['university-admin'],
        allUniversities: false,
        universities: this.universityFilter ? [this.universityFilter] : [],
      }
    },
    openEdit(user) {
      this.formError = ''
      this.editing = {
        id: user.id,
        name: user.name || '',
        email: user.email || '',
        roles: [...(user.roles || [])],
        allUniversities: Boolean(user.all_universities),
        universities: [...(user.universities || [])],
      }
    },
    closeEditor() {
      if (this.saving) return
      this.editing = null
      this.formError = ''
    },
    validateEditing() {
      if (!this.editing.name || !this.editing.email) {
        return 'Name and email are required'
      }
      if (!this.editing.allUniversities && !this.editing.universities.length) {
        return 'Select at least one university'
      }
      return ''
    },
    async saveUser() {
      const error = this.validateEditing()
      if (error) {
        this.formError = error
        return
      }
      this.saving = true
      this.formError = ''
      this.status = ''
      this.statusError = false
      try {
        const payload = {
          mode: this.editing.id ? 'users_update' : 'users_create',
          name: this.editing.name,
          roles: this.editing.roles,
          allUniversities: this.editing.allUniversities,
          universities: this.editing.universities,
        }
        if (this.editing.id) {
          payload.userId = this.editing.id
        } else {
          payload.email = this.editing.email
        }
        await axios.post('/admin/search', payload)
        this.status = this.editing.id ? 'User updated' : 'User created'
        this.editing = null
        await this.fetchUsers()
      } catch (error) {
        this.formError = (error && error.response && error.response.data && error.response.data.error) || 'Could not save user'
      } finally {
        this.saving = false
      }
    },
    async resetPassword(user) {
      if (!window.confirm(`Reset password for ${user.email}?`)) return
      this.saving = true
      this.status = ''
      this.statusError = false
      try {
        await axios.post('/admin/search', {
          mode: 'users_reset_password',
          userId: user.id,
        })
        this.status = 'Password reset email sent'
      } catch (error) {
        this.status = (error && error.response && error.response.data && error.response.data.error) || 'Could not reset password'
        this.statusError = true
      } finally {
        this.saving = false
      }
    },
    async disableUser(user) {
      if (!window.confirm(`Disable access for ${user.email}?`)) return
      this.saving = true
      this.status = ''
      this.statusError = false
      try {
        await axios.post('/admin/search', {
          mode: 'users_disable',
          userId: user.id,
        })
        this.status = 'User disabled'
        await this.fetchUsers()
      } catch (error) {
        this.status = (error && error.response && error.response.data && error.response.data.error) || 'Could not disable user'
        this.statusError = true
      } finally {
        this.saving = false
      }
    },
    formatList(values) {
      return (values || []).length ? values.join(', ') : 'None'
    },
    formatRoles(user) {
      if ((user.roles || []).length) return this.formatList(user.roles)
      if (user.all_universities || (user.universities || []).length) return 'Source upload only'
      return 'None'
    },
    formatUniversities(values, user = null) {
      if (user && user.all_universities) return 'All universities'
      if (!(values || []).length) return 'None'
      const namesBySlug = new Map((this.universities || []).map((university) => [university.slug, university.name]))
      return values.map((slug) => namesBySlug.get(slug) || slug).join(', ')
    },
  },
}
</script>
